/**
 * SVG maps of the levels from their definitions: the track line with start and checkpoints, or the training area's
 * riding lines (the oval, the figure eight) with the drills' cones and lines.
 */
import { CenterLine } from '../sim/centerline';
import { LevelDef } from '../sim/level';
import { LEVELS, LevelId } from '../sim/levels';
import { fitProjector, pathData, Project, XZ } from './logic/minimap';

const NS = 'http://www.w3.org/2000/svg';
const el = (tag: string, attrs: Record<string, string | number>) => {
  const e = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
};

interface Geometry { line: XZ[] | null; closed: boolean; checkpoints: XZ[]; start: XZ | null; dots: XZ[]; segments: [XZ, XZ][]; loops: XZ[][] }
const cache = new Map<LevelId, Geometry>();

function geometry(id: LevelId): Geometry {
  const hit = cache.get(id);
  if (hit) return hit;
  const def: LevelDef = LEVELS[id];
  let g: Geometry;
  if (def.track && def.map !== 'drills') {
    const line = new CenterLine(def.track.points, 4);
    const pts: XZ[] = [];
    for (let i = 0; i < line.count; i++) pts.push({ x: line.xs[i]!, z: line.zs[i]! });
    const st = def.starts.default!;
    const start = 's' in st ? line.pointAt(st.s) : { x: st.x, z: st.z };
    g = { line: pts, closed: true, checkpoints: def.checkpoints.map((s) => line.pointAt(s)), start, dots: [], segments: [], loops: [] };
  } else {
    const dots: XZ[] = [], segments: [XZ, XZ][] = [];
    for (const p of def.props) {
      if (p.kind === 'line') segments.push([{ x: p.a[0], z: p.a[1] }, { x: p.b[0], z: p.b[1] }]);
      else dots.push({ x: p.x, z: p.z });
    }
    const loops = (def.paths ?? []).map((path) => {
      const l = new CenterLine(path.points, 2), pts: XZ[] = [];
      for (let i = 0; i < l.count; i++) pts.push({ x: l.xs[i]!, z: l.zs[i]! });
      return pts;
    });
    g = { line: null, closed: false, checkpoints: [], start: null, dots, segments, loops };
  }
  cache.set(id, g);
  return g;
}

export class LevelMap {
  readonly svg: SVGSVGElement;
  private readonly project: Project;
  private readonly rider: SVGElement;

  constructor(id: LevelId, w = 200, h = 140, cls = 'map') {
    const g = geometry(id);
    const all = g.line ?? [...g.dots, ...g.segments.flat(), ...g.loops.flat()];
    this.project = fitProjector(all, w, h, 10);
    this.svg = el('svg', { viewBox: `0 0 ${w} ${h}`, class: cls, 'aria-hidden': 'true' }) as SVGSVGElement;
    if (g.line) {
      const d = pathData(g.line, this.project, g.closed);
      this.svg.append(el('path', { d, class: 'map-casing' }), el('path', { d, class: 'map-line' }));
      g.checkpoints.forEach((c, i) => {
        const [x, y] = this.project(c.x, c.z);
        this.svg.append(el('circle', { cx: x, cy: y, r: i === 0 ? 0 : 3, class: 'map-cp' }));
      });
      if (g.start) {
        const [x, y] = this.project(g.start.x, g.start.z);
        this.svg.append(el('rect', { x: x - 5, y: y - 5, width: 10, height: 10, class: 'map-start', transform: `rotate(45 ${x} ${y})` }));
      }
    } else {
      this.svg.append(el('circle', { cx: w / 2, cy: h / 2, r: Math.min(w, h) / 2 - 4, class: 'map-pad' }));
      for (const loop of g.loops) {
        const d = pathData(loop, this.project, true);
        this.svg.append(el('path', { d, class: 'map-casing' }), el('path', { d, class: 'map-line' }));
      }
      for (const [a, b] of g.segments) {
        const [x1, y1] = this.project(a.x, a.z), [x2, y2] = this.project(b.x, b.z);
        this.svg.append(el('line', { x1, y1, x2, y2, class: 'map-seg' }));
      }
      for (const d of g.dots) {
        const [x, y] = this.project(d.x, d.z);
        this.svg.append(el('circle', { cx: x, cy: y, r: 2.2, class: 'map-dot' }));
      }
    }
    this.rider = el('circle', { cx: -99, cy: -99, r: 5, class: 'map-rider' });
    this.svg.append(this.rider);
  }

  setRider(x: number, z: number): void {
    const [sx, sy] = this.project(x, z);
    this.rider.setAttribute('cx', sx.toFixed(1));
    this.rider.setAttribute('cy', sy.toFixed(1));
  }
}
